import { createFileRoute } from '@tanstack/react-router';
import { getSession, useGetAllProfileQuery, useGetSpace, useGetSubSpaces } from '@onesource/services';
import { SpaceType } from '@onesource/schemas';
import { CommunityTabContent } from '../../../../../features/community-hub/components/community-tab-content';
import { FeedTabContent } from '../../../../../features/community-hub/components/feed-tab-content';
import { SpaceNoAccessPage } from '../../../../../features/community-hub/components/space-no-access-page';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

/**
 * Space Community Route
 * Path: /community-hub/spaces/:id/community
 *
 * Handles three cases:
 * 1. If user has no access -> shows space name + banner with lock overlay
 * 2. If :id is a CONTAINER space -> shows sub-tabs for each FEED sub-space
 * 3. If :id is a FEED space -> shows posts directly from that feed
 */
export const Route = createFileRoute('/_protected/community-hub/spaces/$id/community')({
  component: SpaceCommunityRoute,
});

function SpaceCommunityRoute() {
  const { id } = Route.useParams();
  const token = getSession();
  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(id);
  const { data: profile } = useGetAllProfileQuery({ token });

  const isContainer = space?.spaceType === SpaceType.CONTAINER;

  // Access is determined by whether the user has an entry in profile.access for the space's app.
  // Sub-spaces don't carry app directly — inherit from parent. profile.access is built from user_access.
  const spaceApp = space?.app ?? space?.parent?.app;
  const hasAccess = spaceApp ? !!profile?.access?.[spaceApp] : false;

  const { data: subSpaces, isLoading: isLoadingSubSpaces } = useGetSubSpaces(id, {
    enabled: hasAccess && isContainer,
  });

  if (isLoadingSpace || (hasAccess && isContainer && isLoadingSubSpaces)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (spaceError) {
    return <Alert severity="error">Failed to load space: {spaceError.message}</Alert>;
  }

  if (!space) {
    return <Alert severity="error">Space not found</Alert>;
  }

  // No access - show locked page
  if (!hasAccess) {
    return <SpaceNoAccessPage name={space.name} bannerImage={space.bannerImage ?? null} />;
  }

  // If this is a FEED space, show posts directly
  if (space.spaceType === SpaceType.FEED) {
    return <FeedTabContent subSpace={space} parentSpace={space} />;
  }

  // If this is a CONTAINER space, show sub-tabs for each FEED sub-space
  const feedSubSpaces = subSpaces?.filter(s => s.spaceType === SpaceType.FEED) || [];

  return <CommunityTabContent subSpaces={feedSubSpaces} parentSpace={space} />;
}
